import { DownyMildewDay, DownyMildewRiskLevel } from '../../models/models';

export type CssStyling = { [key: string]: string } | null | undefined;

const defaultStyling: CssStyling = { color: '#18181B', fontFamily: 'Akkurat', fontSize: '12px' };

/* Risk level styling */
const unknownStyling: CssStyling = { ...defaultStyling, 'background-color': '#FAFAFA' };
const lowStyling: CssStyling = { ...defaultStyling, 'background-color': '#FAFAFA' };
const moderateStyling: CssStyling = { ...defaultStyling, 'background-color': '#fff0b0' };
const moderateHighStyling: CssStyling = { ...defaultStyling, 'background-color': '#f49433' };
const highStyling: CssStyling = { ...defaultStyling, 'background-color': '#b5061a', color: 'white' };

/* Spray advice styling */
const noAdviceStyling: CssStyling = { ...defaultStyling, 'background-color': '#FAFAFA' };
const sprayRecommendedStyling: CssStyling = { ...defaultStyling, 'background-color': '#b5061a', color: 'white' };

const cropsProtectedStyling = (sprayProtection: number): CssStyling => ({
  ...defaultStyling,
  'background-color': sprayProtection === 0 ? '#FAFAFA' : `rgba(39, 144, 73, ${sprayProtection})`,
  color: sprayProtection > 0.5 ? '#FAFAFA' : '#18181B',
});

export const isAdviceHidden = (
  day: {
    sprayRecommended?: boolean;
    infectionRiskPercentage?: number | null;
    infectionRiskLevel?: string | null;
    combinedRiskPercentage?: number | null;
    combinedRiskLevel?: string | null;
  },
): boolean =>
  day.sprayRecommended === false &&
  day.infectionRiskPercentage == null &&
  day.infectionRiskLevel == null &&
  day.combinedRiskPercentage == null &&
  day.combinedRiskLevel == null;

const sprayAdviceTranslationConfig: Record<
  DownyMildewDay['sprayAdvice'],
  { label: string; tooltip: string | null }
> = {
  Protected: {
    label: 'downyMildew.sprayProtectionAdvice.protected',
    tooltip: 'downyMildew.sprayProtectionAdvice.tooltip.protected',
  },
  Unprotected: {
    label: 'downyMildew.sprayProtectionAdvice.notProtected',
    tooltip: 'downyMildew.sprayProtectionAdvice.tooltip.unprotected',
  },
  SprayRecommended: {
    label: 'downyMildew.sprayProtectionAdvice.recommended',
    tooltip: 'downyMildew.sprayProtectionAdvice.tooltip.sprayRecommended',
  },
  OutsideGrowthStage: {
    label: 'downyMildew.sprayProtectionAdvice.outsideGrowthStage',
    tooltip: 'downyMildew.sprayProtectionAdvice.tooltip.outsideGrowthStage',
  },
  NoAdvice: {
    label: 'downyMildew.sprayProtectionAdvice.noAdvice',
    tooltip: null,
  },
};

export const getAdviceTranslationKey = (day: DownyMildewDay): string =>
  sprayAdviceTranslationConfig[day.sprayAdvice]?.label ??
  sprayAdviceTranslationConfig.NoAdvice.label;

export const getAdviceTooltipKey = (day: DownyMildewDay): string | null =>
  sprayAdviceTranslationConfig[day.sprayAdvice]?.tooltip ?? null;

export const getAdviceColorKey = (day: DownyMildewDay): CssStyling => {
  if (day.sprayAdvice === 'NoAdvice' || day.sprayAdvice === 'OutsideGrowthStage') {
    return noAdviceStyling;
  }

  if (day.sprayAdvice === 'Protected') {
    return cropsProtectedStyling(day.sprayProtectionPercentage);
  }

  if (day.sprayAdvice === 'SprayRecommended') {
    return sprayRecommendedStyling;
  }

  if (day.combinedRiskLevel === 'Low') {
    return lowStyling;
  }

  if (day.combinedRiskLevel === 'Moderate') {
    return moderateStyling;
  }

  if (day.combinedRiskLevel === 'ModerateHigh') {
    return moderateHighStyling;
  }

  if (day.combinedRiskLevel === 'High') {
    return highStyling;
  }

  return noAdviceStyling;
}

export const getRiskLevelStyling = (riskLevel: DownyMildewRiskLevel): CssStyling => {
  switch (riskLevel) {
    case 'High':
      return highStyling;
    case 'ModerateHigh':
      return moderateHighStyling;
    case 'Moderate':
      return moderateStyling;
    case 'Low':
      return lowStyling;
    default:
      return unknownStyling;
  }
};
